// Updated client/src/pages/Login.js
import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ClubSelection } from "../components/ClubSelection";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const Login = () => {
  const {
    login,
    loginWithCode,
    resetPassword,
    isAuthenticated,
    user,
    getRedirectPath,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clubSelectionData, setClubSelectionData] = useState(null);
  const [passwordResetNeeded, setPasswordResetNeeded] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [loginMode, setLoginMode] = useState("password");
  const [codeFormData, setCodeFormData] = useState({
    email: "",
    accessCode: "",
  });

  // Get the intended path from location state
  const intendedPath = location.state?.from?.pathname;

  // If already authenticated, redirect based on role and intended path
  if (isAuthenticated) {
    const redirectTo = getRedirectPath(user, intendedPath);
    return <Navigate to={redirectTo} replace />;
  }

  const handleClubSelect = async (clubId) => {
    setLoading(true);

    try {
      // Login with specific club
      const result = await login({
        ...formData,
        clubId: clubId,
      });

      if (result.success) {
        // Navigate to intended path or default redirect using result user data
        const redirectTo = getRedirectPath(result.user, intendedPath);
        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      console.error("Club selection error:", error);
      // Reset to login form on error
      setClubSelectionData(null);
    }

    setLoading(false);
  };

  // If we need club selection, show the club selection component
  if (clubSelectionData) {
    return (
      <ClubSelection
        clubs={clubSelectionData.clubs}
        user={clubSelectionData.user}
        onClubSelect={handleClubSelect}
        loading={loading}
      />
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData);

      if (result.requiresPasswordReset) {
        // User has a legacy password hash that needs migration
        setPasswordResetNeeded(true);
        setLoading(false);
        return;
      }

      if (result.success) {
        if (result.requiresPasswordChange) {
          // For password change, let the PasswordChangeGuard handle it
          return;
        }

        // For successful login, redirect based on intended path using the result user data
        const redirectTo = getRedirectPath(result.user, intendedPath);
        navigate(redirectTo, { replace: true });
      } else if (result.requiresClubSelection) {
        // User has multiple clubs, show selection
        setClubSelectionData({
          user: result.user,
          clubs: result.clubs,
        });
      }
    } catch (error) {
      console.error("Login error:", error);
    }

    setLoading(false);
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginWithCode(
        codeFormData.email,
        codeFormData.accessCode.toLocaleUpperCase(),
      );
      if (result.success) {
        const redirectTo = getRedirectPath(result.user, intendedPath);
        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      console.error("Access code login error:", error);
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    setLoading(true);
    const result = await resetPassword(formData.email);
    if (result.success) {
      setResetSent(true);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Password reset required screen
  if (passwordResetNeeded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-36 w-36 flex items-center justify-center">
              <img src="/Logo_words.png" alt="Logo" className="h-36 w-36" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Password Reset Required
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Due to a system upgrade, your password needs to be reset. Click
              below to receive a temporary password via email.
            </p>
          </div>

          {resetSent ? (
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-700 text-center">
                A temporary password has been sent to{" "}
                <strong>{formData.email}</strong>. Please check your email and
                log in with the new password.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => {
                    setPasswordResetNeeded(false);
                    setResetSent(false);
                    setFormData({ ...formData, password: "" });
                  }}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                >
                  Back to Login
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md bg-yellow-50 p-4">
                <p className="text-sm text-yellow-700 text-center">
                  A temporary password will be sent to:{" "}
                  <strong>{formData.email}</strong>
                </p>
              </div>
              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  "Send Temporary Password"
                )}
              </button>
              <button
                onClick={() => setPasswordResetNeeded(false)}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
              >
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-36 w-36 flex items-center justify-center">
            <img src="/Logo_words.png" alt="Logo" className="h-36 w-36" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Eval Ninja - Sports Evaluation Management System
          </p>

          <div className="flex border-b border-gray-200 mt-4">
            <button
              type="button"
              onClick={() => setLoginMode("password")}
              className={`flex-1 py-2 text-sm font-medium text-center border-b-2 ${
                loginMode === "password"
                  ? "border-slate-500 text-slate-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Password Login
            </button>
            <button
              type="button"
              onClick={() => setLoginMode("accessCode")}
              className={`flex-1 py-2 text-sm font-medium text-center border-b-2 ${
                loginMode === "accessCode"
                  ? "border-slate-500 text-slate-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Access Code Login
            </button>
          </div>
        </div>

        {loginMode === "password" && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  disabled={loading}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 focus:z-10 sm:text-sm disabled:opacity-50"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="relative">
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  disabled={loading}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 focus:z-10 sm:text-sm disabled:opacity-50"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  "Sign in"
                )}
              </button>
            </div>
          </form>
        )}

        {loginMode === "accessCode" && (
          <form className="mt-8 space-y-6" onSubmit={handleCodeSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="code-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="code-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  disabled={loading}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 focus:z-10 sm:text-sm disabled:opacity-50"
                  placeholder="Email address"
                  value={codeFormData.email}
                  onChange={(e) =>
                    setCodeFormData({ ...codeFormData, email: e.target.value })
                  }
                />
              </div>
              <div>
                <label htmlFor="accessCode" className="sr-only">
                  Access Code
                </label>
                <input
                  id="accessCode"
                  name="accessCode"
                  type="text"
                  required
                  disabled={loading}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 focus:z-10 sm:text-sm disabled:opacity-50"
                  placeholder="Evaluation Access Code"
                  value={codeFormData.accessCode}
                  onChange={(e) => {
                    setCodeFormData({
                      ...codeFormData,
                      accessCode: e.target.value.toUpperCase(),
                    });
                  }}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  "Sign in with Access Code"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
